import { useEffect, useRef, useState } from "react";

import type { FollowUpSuggestionsResponse } from "../services/api";

interface UseStarterSuggestionChipsOptions {
  enabled: boolean;
  workspaceId: string;
  /** True only for a new chat with no messages that is not loading anything. */
  isEmptyChat: boolean;
  fetchSuggestions: (
    workspaceId: string,
    signal?: AbortSignal,
  ) => Promise<FollowUpSuggestionsResponse>;
}

export const STARTER_SUGGESTIONS_STORAGE_PREFIX = "ida-starter-suggestions:";
/** Stored starters older than this are not shown while the refresh runs. */
export const STARTER_SUGGESTIONS_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const cleanSuggestions = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is string =>
          typeof item === "string" && item.trim() !== "",
      )
    : [];

const readStoredStarters = (workspaceId: string): string[] | null => {
  try {
    const stored = window.localStorage.getItem(
      STARTER_SUGGESTIONS_STORAGE_PREFIX + workspaceId,
    );
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    const savedAt = Number(parsed?.savedAt);
    if (
      !Number.isFinite(savedAt) ||
      Date.now() - savedAt > STARTER_SUGGESTIONS_MAX_AGE_MS
    ) {
      return null;
    }
    const suggestions = cleanSuggestions(parsed?.suggestions);
    return suggestions.length > 0 ? suggestions : null;
  } catch {
    // Ignore malformed or inaccessible localStorage.
    return null;
  }
};

const writeStoredStarters = (workspaceId: string, suggestions: string[]) => {
  try {
    const key = STARTER_SUGGESTIONS_STORAGE_PREFIX + workspaceId;
    if (suggestions.length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(
      key,
      JSON.stringify({ savedAt: Date.now(), suggestions }),
    );
  } catch {
    // Non-critical cache persistence failure.
  }
};

/**
 * Starter question chips for a new, empty IDA chat.
 *
 * Starters from an earlier visit (kept per workspace in localStorage for a
 * day) show at once, and are refreshed in the background once per workspace
 * while this view stays mounted, so starting several new chats costs one model
 * call. The refresh keeps running if the user sends a question first, so the
 * next new chat has fresh starters. A failed request is not cached and is
 * retried the next time an empty chat is shown.
 */
export function useStarterSuggestionChips({
  enabled,
  workspaceId,
  isEmptyChat,
  fetchSuggestions,
}: UseStarterSuggestionChipsOptions): string[] {
  const [suggestionsByWorkspace, setSuggestionsByWorkspace] = useState<
    Record<string, string[]>
  >({});
  const refreshedRef = useRef(new Set<string>());
  const inFlightRef = useRef(new Set<string>());
  const controllerRef = useRef<AbortController | null>(null);

  // Declared before the fetch effect so the controller exists when it runs.
  useEffect(() => {
    const controller = new AbortController();
    const inFlight = inFlightRef.current;
    controllerRef.current = controller;
    return () => {
      controller.abort();
      // A StrictMode remount must be able to start the request again.
      inFlight.clear();
    };
  }, []);

  useEffect(() => {
    if (!enabled || !isEmptyChat || !workspaceId || !controllerRef.current) {
      return;
    }
    if (
      refreshedRef.current.has(workspaceId) ||
      inFlightRef.current.has(workspaceId)
    ) {
      return;
    }

    const stored = readStoredStarters(workspaceId);
    if (stored) {
      setSuggestionsByWorkspace((previous) =>
        workspaceId in previous
          ? previous
          : { ...previous, [workspaceId]: stored },
      );
    }

    const { signal } = controllerRef.current;
    inFlightRef.current.add(workspaceId);
    fetchSuggestions(workspaceId, signal)
      .then((response) => {
        if (signal.aborted) return;
        const suggestions = cleanSuggestions(response?.suggestions);
        refreshedRef.current.add(workspaceId);
        writeStoredStarters(workspaceId, suggestions);
        setSuggestionsByWorkspace((previous) => ({
          ...previous,
          [workspaceId]: suggestions,
        }));
      })
      .catch(() => {
        // Optional garnish: a failed request keeps whatever is shown.
      })
      .finally(() => {
        if (!signal.aborted) {
          inFlightRef.current.delete(workspaceId);
        }
      });
  }, [enabled, isEmptyChat, workspaceId, fetchSuggestions]);

  if (!enabled || !isEmptyChat) {
    return [];
  }
  return suggestionsByWorkspace[workspaceId] ?? [];
}
